import * as React from "react";
import {
  SourceCard,
  FileDropZone,
  UploadQueue,
  PasteTextPanel,
  UrlPanel,
  type UploadFile,
} from "@aisquare/ui";
import { FileText, Globe, HardDrive, Youtube } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

export function SourceIngestionDemo() {
  // Drop zone -> live queue. Picking (or dropping) files appends rows the
  // consumer owns; the family never uploads, so we seed each row "uploading"
  // and let a fake transport tick it to success. Remove / retry are wired so
  // the queue's enter/exit springs are visible on a real mutation.
  const [queue, setQueue] = React.useState<UploadFile[]>([]);

  const addFiles = React.useCallback((files: File[]) => {
    const rows: UploadFile[] = files.map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      name: f.name,
      size: `${Math.max(1, Math.round(f.size / 1024))} KB`,
      type: f.type,
      status: "uploading",
      progress: 0,
    }));
    setQueue((q) => [...q, ...rows]);
    // Stand in for a real transport: resolve each new row to success after a tick.
    rows.forEach((row) => {
      setTimeout(() => {
        setQueue((q) =>
          q.map((it) =>
            it.id === row.id ? { ...it, status: "success", progress: 100 } : it,
          ),
        );
      }, 1200);
    });
  }, []);

  // Paste-text panel, controlled — drives the live character counter; submit
  // is disabled until the content is non-empty.
  const [pasteText, setPasteText] = React.useState("");
  const [pasteName, setPasteName] = React.useState("");

  // URL panel, controlled — seeded with one valid + one invalid line so the
  // "links detected" counter shows; submitting surfaces the verbatim-ported
  // validator error banner.
  const [urls, setUrls] = React.useState("https://example.com\nnot-a-url");

  return (
    <Section
      id="source-ingestion"
      title="SourceIngestion"
      description="The vault add-a-source family as an app-decoupled unit: a source-picker grid of SourceCards, a zero-dependency FileDropZone feeding a controlled UploadQueue, and back-able Paste / URL panels. Ported from Creator Studio's vault upload flow; every piece is controlled (the consumer owns the queue + panel values and wires its own transport) and the URL validator is kept verbatim."
    >
      <VariantBlock
        label="Source grid"
        caption="the picker surface — each SourceCard opens a panel; Drive shows a connect badge, gated sources read Soon"
      >
        <div className="grid w-full max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
          <SourceCard
            icon={<FileText className="size-5" aria-hidden />}
            title="Paste text"
            description="Add text content directly"
            onClick={() => {}}
          />
          <SourceCard
            icon={<Globe className="size-5" aria-hidden />}
            title="Webpage / GitHub URL"
            description="Import content from a URL"
            onClick={() => {}}
          />
          <SourceCard
            icon={<HardDrive className="size-5" aria-hidden />}
            title="Google Drive"
            description="Connect and import"
            connectionStatus="connect"
            onConnect={() => {}}
            onClick={() => {}}
          />
          <SourceCard
            icon={<Youtube className="size-5" aria-hidden />}
            title="YouTube"
            description="Disabled until enabled"
            disabled
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Drop zone -> queue"
        caption="drop or browse files — rows append to a controlled UploadQueue and a fake transport ticks each to success"
      >
        <div className="flex w-full max-w-xl flex-col gap-4">
          {queue.length === 0 ? (
            <FileDropZone accept=".pdf,.docx,.txt,.csv,.md" onDrop={addFiles} />
          ) : (
            <FileDropZone compact onDrop={addFiles} />
          )}
          <UploadQueue
            items={queue}
            onRetry={(id) =>
              setQueue((q) =>
                q.map((it) =>
                  it.id === id ? { ...it, status: "success", progress: 100 } : it,
                ),
              )
            }
            onRemove={(id) => setQueue((q) => q.filter((it) => it.id !== id))}
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Paste text panel"
        caption="controlled — live character counter; submit is disabled until the content is non-empty"
      >
        <div className="w-full max-w-2xl rounded-lg border border-border p-4">
          <PasteTextPanel
            text={pasteText}
            onTextChange={setPasteText}
            fileName={pasteName}
            onFileNameChange={setPasteName}
            onBack={() => {}}
            onSubmit={() => {}}
          />
        </div>
      </VariantBlock>

      <VariantBlock
        label="URL panel"
        caption="controlled — live link counter; submit runs the verbatim validator (the seeded bad line trips the error banner)"
      >
        <div className="w-full max-w-2xl rounded-lg border border-border p-4">
          <UrlPanel value={urls} onChange={setUrls} onBack={() => {}} onSubmit={() => {}} />
        </div>
      </VariantBlock>
    </Section>
  );
}
